import React from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dispositivo } from '../types/dispositivos.types';
import { EtiquetaEditable } from './EtiquetaEditable';
import {
  haceCuanto,
  momento,
  motivoLegible,
  rolLegible,
  SonarLocal,
  sonarDisponible,
  textoEstado,
  textoNavegador,
  textoSonar,
  textoVersion,
  TONO_ESTADO,
} from '../utils/dispositivos';

/**
 * Las piezas de un equipo, compartidas por la fila de la tabla (escritorio) y
 * la tarjeta (teléfono): el dato es el mismo, sólo cambia la disposición.
 */

export const Codigo: React.FC<{ codigo: string; nuevo: boolean }> = ({ codigo, nuevo }) => (
  <div className="disp-codigo-linea">
    <span className="disp-codigo">
      <span className="disp-codigo-pre">Equipo</span> <span className="disp-codigo-valor">{codigo}</span>
    </span>
    {nuevo && (
      <Tag
        value="Nuevo"
        severity="info"
        className="disp-nuevo"
        title="Visto por primera vez hace menos de 24 h. Un equipo perdido que reaparece con los datos del navegador borrados se ve así."
      />
    )}
  </div>
);

export const Etiqueta: React.FC<{
  d: Dispositivo;
  nombre: string;
  onGuardar: (etiqueta: string | null) => Promise<void>;
}> = ({ d, nombre, onGuardar }) => <EtiquetaEditable etiqueta={d.etiqueta} nombre={nombre} onGuardar={onGuardar} />;

export const Estado: React.FC<{ d: Dispositivo; ahora: number }> = ({ d, ahora }) => {
  const tono = TONO_ESTADO[d.estado] ?? 'gris';
  const conectado = d.estado === 'en_pantalla' || d.estado === 'conexion_abierta';
  return (
    <div className="disp-estado" data-estado={d.estado}>
      <div className="disp-estado-principal">
        <span className={`disp-punto disp-punto-${tono}`} aria-hidden="true" />
        <span>{textoEstado(d, ahora)}</span>
      </div>
      {conectado && (
        <div className="disp-tenue">
          {d.sockets === 1 ? '1 conexión' : `${d.sockets} conexiones`}
          {d.ultimaActividadWs ? ` · actividad ${haceCuanto(d.ultimaActividadWs, ahora)}` : ''}
        </div>
      )}
      {d.estado === 'revocado' && d.revocado && (
        <div className="disp-tenue">
          Motivo: {motivoLegible(d.revocado.motivo)}
          {d.revocado.nota ? ` · ${d.revocado.nota}` : ''}
        </div>
      )}
    </div>
  );
};

/**
 * Un correo que se parte en la arroba y no a la mitad de una palabra
 * («vendedor.trtezontle» / «@colectivomoda.com»): en una columna angosta,
 * `overflow-wrap` lo cortaba donde caía.
 */
const Correo: React.FC<{ valor: string | null | undefined }> = ({ valor }) => {
  if (!valor) return <>—</>;
  const i = valor.indexOf('@');
  if (i <= 0) return <>{valor}</>;
  return (
    <>
      {valor.slice(0, i)}
      <wbr />
      {valor.slice(i)}
    </>
  );
};

export const Cuenta: React.FC<{ d: Dispositivo }> = ({ d }) => (
  <div>
    <div className="disp-romper">
      <Correo valor={d.cuenta?.email} />
    </div>
    <div className="disp-tenue">{rolLegible(d.cuenta?.rol)}</div>
  </div>
);

export const Vendedor: React.FC<{ d: Dispositivo; ahora: number }> = ({ d, ahora }) => {
  const v = d.ultimoVendedor;
  if (!v) return <span className="disp-tenue">—</span>;
  return (
    <div>
      <div>{v.nombre || v.id}</div>
      {v.en && <div className="disp-tenue">{haceCuanto(v.en, ahora)}</div>}
    </div>
  );
};

export const Visto: React.FC<{ d: Dispositivo; ahora: number }> = ({ d, ahora }) => (
  <div>
    <div>Última vez: {haceCuanto(d.ultimaVez, ahora)}</div>
    <div className="disp-tenue">Primera vez: {momento(d.primeraVez, ahora)}</div>
  </div>
);

export const VersionApp: React.FC<{ d: Dispositivo }> = ({ d }) => (
  <div>
    <div className="disp-mono">{textoVersion(d.appVersion)}</div>
    <div className="disp-tenue">{textoNavegador(d)}</div>
  </div>
);

interface AccionesProps {
  d: Dispositivo;
  nombre: string;
  ahora: number;
  /** Sólo superadmin y admin: cerrar sesión y reactivar. */
  puedeCerrar: boolean;
  sonar?: SonarLocal;
  onSonar: () => void;
  onAyuda: () => void;
  onCerrarSesion: () => void;
  onReactivar: () => void;
}

export const Acciones: React.FC<AccionesProps> = ({
  d,
  nombre,
  ahora,
  puedeCerrar,
  sonar,
  onSonar,
  onAyuda,
  onCerrarSesion,
  onReactivar,
}) => {
  const disp = sonarDisponible(d);
  const enviando = sonar?.tipo === 'enviando';
  const resultado = textoSonar(sonar, d.ultimoSonar, ahora);
  const revocado = d.estado === 'revocado';
  return (
    <div className="disp-acciones">
      {!revocado && (
        <div className="disp-sonar">
          <Button
            type="button"
            label="Hacer sonar"
            icon="pi pi-volume-up"
            className="p-button-sm"
            onClick={onSonar}
            disabled={!disp.ok || enviando}
            loading={enviando}
            aria-label={`Hacer sonar ${nombre}`}
          />
          <Button
            type="button"
            icon="pi pi-info-circle"
            className="p-button-text p-button-rounded p-button-sm disp-boton-icono"
            onClick={onAyuda}
            aria-label="Límites de «Hacer sonar»"
            tooltip="Lo que sí y lo que no"
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      )}
      {!revocado && !disp.ok && disp.porque && <small className="disp-tenue">{disp.porque}</small>}
      {d.estado === 'conexion_abierta' && !sonar && (
        <small className="disp-tenue">Sin actividad: con la pantalla apagada no suena hasta que la enciendan.</small>
      )}
      {resultado && (
        <small className={`disp-sonar-resultado disp-sonar-${resultado.tono}`} data-testid="sonar-resultado" role="status">
          {resultado.texto}
        </small>
      )}
      {puedeCerrar && !revocado && (
        <Button
          type="button"
          label="Cerrar sesión de este equipo"
          icon="pi pi-sign-out"
          className="p-button-sm p-button-outlined p-button-danger disp-boton-cierre"
          onClick={onCerrarSesion}
          aria-label={`Cerrar sesión de ${nombre}`}
        />
      )}
      {puedeCerrar && revocado && (
        <Button
          type="button"
          label="Reactivar"
          icon="pi pi-replay"
          className="p-button-sm p-button-outlined"
          onClick={onReactivar}
          aria-label={`Reactivar ${nombre}`}
        />
      )}
    </div>
  );
};
